import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import Pager from '../components/Pager';
import StatusInfo from '../components/StatusInfo';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 20;

// N-115: та же семантика статуса счёта, что и на вкладке «По месяцам» страницы
// «Комиссии» — просто счёт создаётся не в конце календарного месяца, а сразу при
// закрытии окна направления (сумма уже известна целиком).
const INVOICE_STATUS = {
  forming: { label: 'Счёт формируется', badge: s => s.badgeGray, tip: 'Направление закрылось, бухгалтер Refero ещё готовит счёт.' },
  invoiced: { label: 'Выставлен', badge: s => s.badgeYellow, tip: 'Счёт сформирован и проверен бухгалтером Refero, ожидает оплаты.' },
  paid: { label: 'Оплачено', badge: s => s.badgeTeal, tip: 'Вы оплатили этот счёт — спасибо!' },
};

export default function Operations() {
  const headers = { Authorization: 'Bearer ' + localStorage.getItem('clinicToken') };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState('window_end');
  const [order, setOrder] = useState('desc');
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(() => {
    const params = { sort, order, limit: LIMIT, offset };
    axios.get(`${API}/clinic-cabinet/operations`, { headers, params })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => {});
    // eslint-disable-next-line
  }, [sort, order, offset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSort = (f) => {
    setOffset(0);
    if (sort === f) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSort(f); setOrder('asc'); }
  };
  const arrow = (f) => (sort !== f ? '' : order === 'asc' ? ' ▲' : ' ▼');

  const download = async (invoiceId, e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await axios.get(`${API}/clinic-cabinet/operation-invoices/${invoiceId}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'invoice.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // счёт не найден/недоступен — молча игнорируем
    } finally {
      setBusy(false);
    }
  };

  const cols = '1.6fr 1.4fr 1fr 1fr 1.2fr 1fr';

  return (
    <Layout>
      <h1 style={s.h1}>Операции</h1>
      <p style={s.subtitle}>
        Отдельная программа направлений — врач направляет пациента на конкретную операцию/процедуру,
        комиссия считается один раз по факту от суммы всех его оплат за установленный период
        (не связано с обычными направлениями и квотой по первичным). Условия программы —
        на дашборде, вкладка «Операционные направления».
      </p>

      <div style={s.table}>
        <div style={{ ...s.head, gridTemplateColumns: cols }}>
          <div>Врач</div>
          <div style={s.thClickable} onClick={() => toggleSort('window_end')}>Период{arrow('window_end')}</div>
          <div>Сумма оплат</div>
          <div style={s.thClickable} onClick={() => toggleSort('total_to_pay')}>К оплате{arrow('total_to_pay')}</div>
          <div>
            Статус счёта
            <StatusInfo title="Статус счёта" items={Object.entries(INVOICE_STATUS).map(([, v]) => [v.label, v.tip])} />
          </div>
          <div>Действие</div>
        </div>
        {items.map((r) => {
          const st = INVOICE_STATUS[r.status] || INVOICE_STATUS.forming;
          return (
            <div key={r.referral_id} style={{ ...s.row, gridTemplateColumns: cols }}>
              <div>
                <div style={s.strong}>{r.doctor_name}</div>
                <div style={s.refId}>{r.doctor_public_id || '—'} · REF-{r.referral_id}</div>
              </div>
              <div style={s.muted}>{new Date(r.window_start).toLocaleDateString('ru')} – {new Date(r.window_end).toLocaleDateString('ru')}</div>
              <div>{fmtMoney(r.total_amount)} ₽</div>
              <div style={s.money}>{fmtMoney(r.total_to_pay)} ₽</div>
              <div><span style={st.badge(s)}>{st.label}</span></div>
              <div>
                <button
                  style={{ ...s.btnGhostSmall, ...(!['invoiced', 'paid'].includes(r.status) ? s.btnDisabled : {}) }}
                  disabled={busy || !['invoiced', 'paid'].includes(r.status)}
                  onClick={(e) => download(r.invoice_id, e)}
                >Скачать счёт</button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div style={s.empty}>Закрытых направлений на операции пока нет</div>}
        <div style={{ padding: '0 20px 20px' }}>
          <Pager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
        </div>
      </div>
    </Layout>
  );
}

const s = {
  h1: { color: '#1B2B4B', fontSize: 24, fontWeight: 700, marginBottom: 8 },
  subtitle: { color: '#64748b', fontSize: 13, lineHeight: 1.5, marginBottom: 20, maxWidth: 720 },
  table: { background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 16px rgba(27,43,75,0.05)' },
  head: { display: 'grid', padding: '14px 20px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eef2f7', alignItems: 'center' },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #f4f7fb', fontSize: 14, color: '#1B2B4B', alignItems: 'center' },
  strong: { fontWeight: 600 },
  refId: { fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#94a3b8', marginTop: 2 },
  money: { color: '#149e90', fontWeight: 600 },
  muted: { color: '#94a3b8', fontSize: 13 },
  badgeTeal: { background: '#e9faf7', color: '#149e90', padding: '3px 10px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeGray: { background: '#f1f5f9', color: '#64748b', padding: '3px 10px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeYellow: { background: '#fef3c7', color: '#b45309', padding: '3px 10px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  btnGhostSmall: { background: 'transparent', color: '#149e90', border: '1px solid #149e90', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDisabled: { color: '#cbd5e1', border: '1px solid #e2e8f0', cursor: 'default' },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '40px 0' },
};
